import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useSearch } from "@/context/SearchContext";
import {
  ShieldCheckered, SquaresFour, Warning, HardDrives, ChartLineUp,
  SignOut, List, MagnifyingGlass, Bell, UsersThree, Crosshair, Broadcast, Skull,
  Code, Newspaper, Brain, Binoculars, ListDashes,
} from "@phosphor-icons/react";

const NAV = [
  { to: "/", label: "Overview", icon: SquaresFour, end: true, testid: "nav-overview" },
  { to: "/pentest", label: "Run Pentest", icon: Crosshair, testid: "nav-pentest" },
  { to: "/intercept", label: "Intercept Elite", icon: Broadcast, testid: "nav-intercept" },
  { to: "/redteam", label: "Red Team", icon: Skull, testid: "nav-redteam" },
  { to: "/codescan", label: "Code Scan", icon: Code, testid: "nav-codescan" },
  { to: "/ndays", label: "N-Days / CVE", icon: Newspaper, testid: "nav-ndays" },
  { to: "/ml", label: "ML Detection", icon: Brain, testid: "nav-ml" },
  { to: "/intel", label: "Threat Intel", icon: Binoculars, testid: "nav-intel" },
  { to: "/incidents", label: "Incidents", icon: Warning, testid: "nav-incidents" },
  { to: "/endpoints", label: "Endpoints", icon: HardDrives, testid: "nav-assets" },
  { to: "/events", label: "Events", icon: ListDashes, testid: "nav-events" },
  { to: "/analytics", label: "Analytics", icon: ChartLineUp, testid: "nav-analytics" },
  { to: "/users", label: "Operators", icon: UsersThree, testid: "nav-users", adminOnly: true },
];

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const { query, setQuery } = useSearch();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex bg-[#05050A] text-white font-mono">
      {/* Sidebar */}
      <aside
        className={`${collapsed ? "w-[68px]" : "w-60"} shrink-0 bg-[#08080D] border-r border-[#1F1F33] flex flex-col transition-all duration-200 sticky top-0 h-screen`}
        data-testid="sidebar"
      >
        <div className="h-16 flex items-center gap-2.5 px-4 border-b border-[#1F1F33]">
          <ShieldCheckered size={26} weight="duotone" color="#00F0FF" className="shrink-0" />
          {!collapsed && (
            <span className="font-head font-bold tracking-tighter text-sm truncate">
              SENTINEL<span className="text-[#00F0FF]">/</span>SOC
            </span>
          )}
        </div>

        <nav className="flex-1 py-4 flex flex-col gap-1">
          {NAV.filter((item) => !item.adminOnly || user?.role === "admin").map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={item.testid}
              className={({ isActive }) =>
                `group flex items-center gap-3 px-4 py-2.5 text-sm transition-all duration-200 border-l-2 ${
                  isActive
                    ? "border-[#00F0FF] bg-[#00F0FF]/5 text-white"
                    : "border-transparent text-[#71717A] hover:text-white hover:border-[#33334D]"
                }`
              }
            >
              <item.icon size={18} weight="duotone" className="shrink-0" />
              {!collapsed && <span className="tracking-wide">{item.label}</span>}
            </NavLink>
          ))}
        </nav>

        <div className="p-3 border-t border-[#1F1F33]">
          <button
            data-testid="logout-button"
            onClick={doLogout}
            className="w-full flex items-center gap-3 px-2 py-2.5 text-sm text-[#71717A] hover:text-[#FF3B30] transition-colors"
          >
            <SignOut size={18} weight="duotone" className="shrink-0" />
            {!collapsed && <span>Sign Out</span>}
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 sticky top-0 z-20 flex items-center justify-between px-5 border-b border-[#1F1F33] bg-[#05050A]/80 backdrop-blur-xl">
          <div className="flex items-center gap-4">
            <button
              data-testid="sidebar-toggle"
              onClick={() => setCollapsed((c) => !c)}
              className="text-[#71717A] hover:text-white transition-colors"
            >
              <List size={20} />
            </button>
            <div className="hidden md:flex items-center gap-2 bg-[#0C0C14] border border-[#1F1F33] px-3 py-1.5 w-72">
              <MagnifyingGlass size={15} color="#71717A" />
              <input
                data-testid="global-search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search threats, assets, IPs…"
                className="bg-transparent outline-none text-xs w-full placeholder:text-[#71717A]"
              />
            </div>
          </div>

          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[#00F0FF]">
              <span className="w-2 h-2 bg-[#00F0FF] pulse-dot" /> Systems Online
            </div>
            <button className="relative text-[#71717A] hover:text-white transition-colors" data-testid="notifications-button">
              <Bell size={18} weight="duotone" />
              <span className="absolute -top-1 -right-1 w-2 h-2 bg-[#FF3B30]" />
            </button>
            <div className="flex items-center gap-2 pl-3 border-l border-[#1F1F33]" data-testid="user-badge">
              <div className="w-8 h-8 bg-[#12121E] border border-[#1F1F33] flex items-center justify-center text-[#00F0FF] text-xs font-bold">
                {(user?.name || user?.email || "?").slice(0, 1).toUpperCase()}
              </div>
              <div className="hidden sm:block leading-tight">
                <div className="text-xs font-bold">{user?.name || "Operator"}</div>
                <div className="text-[10px] text-[#71717A] uppercase tracking-wider">{user?.role || "analyst"}</div>
              </div>
            </div>
          </div>
        </header>

        <main className="flex-1 p-5 lg:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
